import React, {Component} from 'react';
import classNames from 'classnames';
import {useTranslations} from 'next-intl';
import {Grid} from '@/grid';
import Link from '@/links';
import Container from '@/container';
import {PNoMargin} from '@/typography';
import {
  withFloatRightToCenter,
  withTextLeftToCenter,
  withTextLeftToCenterXl
} from '@/hocs/withAlignment';
import styles from './copyright.module.scss';

type StyledTagProps = React.HTMLAttributes<HTMLElement>;

type CopyrightProps = {
  variant?: string;
  linkColor?: string;
};

/** Grid forwards `children` via createProps; PropTypes-only export omits it from types. */
const FooterGrid = Grid as React.ComponentType<{
  fluid?: boolean;
  className?: string;
  tagName?: string;
  children?: React.ReactNode;
}>;

/* withStyles alignment HOCs accept intrinsic tag names at runtime; typings are still JS-only. */
const CopyRightUl = withFloatRightToCenter(
  withTextLeftToCenterXl('ul' as never)
) as React.ComponentType<StyledTagProps>;
const CopyRightDiv = withTextLeftToCenter('div' as never) as React.ComponentType<StyledTagProps>;

class Copyright extends Component<CopyrightProps> {
  static hideCookieBanner() {
    const banner = document.getElementById('hs-eu-cookie-confirmation');
    if (!banner) return;

    banner.classList.add('hs-hidden');
    banner.style.setProperty('display', 'none', 'important');
  }

  static isCookieSettingsModalOpen() {
    const modal = document.getElementById('hs-modal');
    if (!modal) return false;

    return !modal.classList.contains('hs-hidden') && modal.style.display !== 'none';
  }

  static isCookieBannerVisible() {
    const banner = document.getElementById('hs-eu-cookie-confirmation');
    if (!banner) return false;

    return !banner.classList.contains('hs-hidden') && banner.style.display !== 'none';
  }

  static openCookieSettingsModal() {
    // After a saved choice, HubSpot leaves the settings button in the page but
    // ignores clicks until showBanner runs. That call reopens the popup itself.
    if (Copyright.isCookieSettingsModalOpen()) {
      Copyright.hideCookieBanner();
      return true;
    }

    const settingsButton = document.getElementById('hs-eu-cookie-settings-button');
    if (!settingsButton || !Copyright.isCookieBannerVisible()) return false;

    settingsButton.click();
    Copyright.hideCookieBanner();
    return true;
  }

  /* istanbul ignore next */
  static showCookieSettingsPopup(event: React.MouseEvent<HTMLAnchorElement>) {
    event.preventDefault();

    if (Copyright.openCookieSettingsModal()) return;

    // The settings control is missing, or HubSpot has dismissed the bar and will
    // ignore a click until showBanner runs. Ask HubSpot to mount it, then open
    // the settings modal and keep the bar hidden.
    // eslint-disable-next-line no-multi-assign
    const _hsp = (window._hsp = window._hsp || []); // NOSONAR - This is a valid use of window._hsp
    _hsp.push(['showBanner']);

    if (Copyright.openCookieSettingsModal()) return;

    const observer = new MutationObserver(() => {
      if (Copyright.openCookieSettingsModal()) observer.disconnect();
    });
    observer.observe(document.body, {childList: true, subtree: true});
    window.setTimeout(() => observer.disconnect(), 3000);
  }

  render() {
    const {variant, linkColor = 'underlined'} = this.props;

    const CopyrightContent = () => {
      const t = useTranslations('shared.copyright');
      const currentYear = new Date().getFullYear();

      return (
        <>
          <PNoMargin>
            © Workable Technology Limited 2012-{currentYear}
          </PNoMargin>

          <CopyRightUl className={styles['list-inline']}>
            <li>
              <Link href="/legal" variant={linkColor}>
                {t('legal')}
              </Link>
            </li>
            <li>
              <Link href="/privacy" variant={linkColor}>
                {t('privacyPolicy')}
              </Link>
            </li>
            <li>
              <Link href="#" variant={linkColor} onClick={Copyright.showCookieSettingsPopup}>
                {t('cookieSettings')}
              </Link>
            </li>
            <li>
              <Link href="/workable-data-selling-CCPA-policy" variant={linkColor}>
                {t('doNotSell')}
              </Link>
            </li>
            <li>
              <Link href="/modern-slavery-statement" variant={linkColor}>
                {t('modernSlaveryStatement')}
              </Link>
            </li>
          </CopyRightUl>
        </>
      );
    };

    return (
      <CopyRightDiv
        className={classNames(
          styles['footer-notes'],
          {
            [styles[`footer-notes--${variant}`]]: variant !== undefined
          },
          'no-print'
        )}>
        <Container>
          <FooterGrid fluid>
            <div className={styles['footer-notes__inner']}>
              <CopyrightContent />
            </div>
          </FooterGrid>
        </Container>
      </CopyRightDiv>
    );
  }
}

export default Copyright;
